/**
 * Anonymous user tracking with friendly identifiers
 *
 * Handles:
 * - Friendly anonymous ID generation (e.g., "swift-amber-falcon")
 * - Backwards compatibility with legacy anon-XXXXXX format
 * - Basic browser/device info collection
 * - Clean API payload construction
 */

// =============================================================================
// Word Lists for Friendly ID Generation
// =============================================================================

/**
 * Word lists for generating friendly anonymous IDs.
 * Format: adjective-adjective-noun (e.g., "swift-amber-falcon")
 * Combinations: 96 adjectives × 96 adjectives × 100 nouns = ~920,000 unique IDs
 */
const FRIENDLY_ADJECTIVES = [
  // Colors (16)
  'amber',
  'azure',
  'coral',
  'crimson',
  'golden',
  'ivory',
  'jade',
  'pearl',
  'ruby',
  'silver',
  'bronze',
  'copper',
  'indigo',
  'violet',
  'scarlet',
  'teal',
  // Personality (24)
  'bold',
  'brave',
  'bright',
  'calm',
  'clever',
  'eager',
  'gentle',
  'happy',
  'keen',
  'kind',
  'lively',
  'merry',
  'noble',
  'proud',
  'quick',
  'quiet',
  'ready',
  'sharp',
  'smart',
  'steady',
  'swift',
  'warm',
  'wise',
  'witty',
  // Nature (24)
  'alpine',
  'arctic',
  'autumn',
  'coastal',
  'crystal',
  'desert',
  'forest',
  'frozen',
  'lunar',
  'misty',
  'mossy',
  'polar',
  'solar',
  'spring',
  'stellar',
  'stormy',
  'summer',
  'sunny',
  'tropic',
  'winter',
  'windy',
  'wild',
  'woodland',
  'rustic',
  // Physical (16)
  'agile',
  'alert',
  'brisk',
  'deft',
  'fleet',
  'graceful',
  'nimble',
  'plucky',
  'sleek',
  'smooth',
  'snappy',
  'speedy',
  'spry',
  'sturdy',
  'supple',
  'sure',
  // Abstract (16)
  'cosmic',
  'daring',
  'dreamy',
  'epic',
  'fair',
  'fancy',
  'free',
  'fresh',
  'grand',
  'lucky',
  'magic',
  'mighty',
  'prime',
  'royal',
  'scenic',
  'serene',
];

const FRIENDLY_NOUNS = [
  // Birds (26)
  'cardinal',
  'crane',
  'dove',
  'eagle',
  'falcon',
  'finch',
  'hawk',
  'heron',
  'hummingbird',
  'jay',
  'lark',
  'nightingale',
  'osprey',
  'owl',
  'parrot',
  'peacock',
  'pelican',
  'penguin',
  'phoenix',
  'raven',
  'robin',
  'sparrow',
  'swan',
  'swift',
  'thrush',
  'wren',
  // Land animals (22)
  'badger',
  'bear',
  'bison',
  'bobcat',
  'cheetah',
  'coyote',
  'deer',
  'elk',
  'fox',
  'gazelle',
  'hare',
  'jaguar',
  'leopard',
  'lion',
  'lynx',
  'moose',
  'otter',
  'panther',
  'puma',
  'tiger',
  'wolf',
  'zebra',
  // Sea creatures (11)
  'dolphin',
  'marlin',
  'narwhal',
  'orca',
  'salmon',
  'seahorse',
  'seal',
  'shark',
  'stingray',
  'turtle',
  'whale',
  // Nature/Geography (35)
  'aurora',
  'bay',
  'canyon',
  'cascade',
  'cedar',
  'cliff',
  'coast',
  'cove',
  'creek',
  'dune',
  'fjord',
  'glade',
  'glacier',
  'grove',
  'harbor',
  'horizon',
  'island',
  'lagoon',
  'maple',
  'meadow',
  'mesa',
  'mountain',
  'oak',
  'ocean',
  'peak',
  'pine',
  'pond',
  'prairie',
  'reef',
  'ridge',
  'river',
  'shore',
  'summit',
  'valley',
  'willow',
  // Celestial (6)
  'comet',
  'cosmos',
  'meteor',
  'nebula',
  'nova',
  'pulsar',
];

/**
 * Generate a friendly anonymous ID using three words.
 * Format: adjective-adjective-noun (e.g., "swift-amber-falcon")
 *
 * Uses crypto.getRandomValues for secure randomness.
 * ~920,000 unique combinations (96 × 96 × 100)
 */
function generateFriendlyId() {
  // Use crypto for better randomness
  const randomValues = new Uint32Array(3);
  crypto.getRandomValues(randomValues);

  const adj1 = FRIENDLY_ADJECTIVES[randomValues[0] % FRIENDLY_ADJECTIVES.length];
  const adj2 = FRIENDLY_ADJECTIVES[randomValues[1] % FRIENDLY_ADJECTIVES.length];
  const noun = FRIENDLY_NOUNS[randomValues[2] % FRIENDLY_NOUNS.length];

  // Avoid same adjective twice
  if (adj1 === adj2) {
    const altIndex = (randomValues[1] + 1) % FRIENDLY_ADJECTIVES.length;
    return `${adj1}-${FRIENDLY_ADJECTIVES[altIndex]}-${noun}`;
  }

  return `${adj1}-${adj2}-${noun}`;
}

/**
 * Get or create anonymous user ID.
 * New users get friendly format (e.g., "swift-amber-falcon").
 * Existing users keep their legacy format (anon-XXXXXX).
 *
 * Stored in localStorage for persistence across sessions.
 */
export function getAnonymousUserId() {
  const STORAGE_KEY = 'concierge_anon_id';

  // Check if we already have an ID
  let anonId = localStorage.getItem(STORAGE_KEY);

  if (!anonId) {
    // Generate new friendly ID
    anonId = generateFriendlyId();
    localStorage.setItem(STORAGE_KEY, anonId);
    console.log('🆔 [ANALYTICS] Generated new anonymous ID:', anonId);
  }

  return anonId;
}

/**
 * Collect basic browser/device analytics
 * Returns only non-sensitive, standard tracking data
 */
export function getClientAnalytics() {
  const nav = navigator;

  return {
    // Browser info
    user_agent: nav.userAgent,
    browser: getBrowserInfo(),
    platform: nav.platform,

    // Screen info
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,

    // Language
    language: nav.language || nav.userLanguage,

    // Timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Referrer (if any)
    referrer: document.referrer || null,

    // Timestamp
    timestamp: new Date().toISOString(),
  };
}

/**
 * Parse user agent to get simple browser info
 */
function getBrowserInfo() {
  const ua = navigator.userAgent;

  // Simple browser detection
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('MSIE') || ua.includes('Trident')) return 'IE';

  return 'Unknown';
}

/**
 * Build complete analytics payload for API request
 * Combines auth session (if available) with anonymous tracking
 */
export function buildAnalyticsPayload(authSessionId = null) {
  const analytics = {
    // User identification (auth takes priority over anonymous)
    auth_session_id: authSessionId,
    anonymous_id: authSessionId ? null : getAnonymousUserId(),

    // Client metadata
    client_info: getClientAnalytics(),
  };

  return analytics;
}
